import React, { useEffect, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { ChatWidgetAPI } from '../../services/api';

// A website chat visitor's voice message or image (backend chat_media). The file comes from the
// authenticated staff route as a blob, never from a public URL; each message is fetched once per
// page even when it shows in two panels.
const cache = new Map();
const fetchOnce = (conversationId, messageId) => {
  const key = `${conversationId}/${messageId}`;
  if (!cache.has(key)) {
    const pending = ChatWidgetAPI.media(conversationId, messageId);
    pending.catch(() => cache.delete(key));
    cache.set(key, pending);
  }
  return cache.get(key);
};

export const clock = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// One line describing the file, for staff.
export const attachmentLabel = (attachment, lang = 'zh') => {
  const zh = lang === 'zh';
  if (!attachment) return '';
  if (attachment.kind === 'voice') {
    const parts = [zh ? '语音消息' : 'Voice message'];
    if (attachment.duration_ms) parts.push(clock(attachment.duration_ms));
    if (attachment.transcript_status && attachment.transcript_status !== 'ok') parts.push(zh ? '未识别出文字，请直接收听' : 'no transcript, listen to it');
    else parts.push(zh ? '下方文字为自动转写' : 'text below is the transcript');
    return parts.join(' · ');
  }
  const size = attachment.width && attachment.height ? ` · ${attachment.width}×${attachment.height}` : '';
  return `${zh ? '图片（已去除位置等元数据）' : 'Image (location and other metadata removed)'}${size}`;
};

export default function ChatAttachment({ conversationId, message, lang = 'zh' }) {
  const zh = lang === 'zh';
  const attachment = message?.attachment;
  const messageId = message?.turn_id;
  const kind = attachment?.kind;
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!kind || !conversationId || !messageId) return undefined;
    let alive = true;
    let objectUrl = null;
    setFailed(false);
    fetchOnce(conversationId, messageId)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, conversationId, messageId]);

  if (!attachment) return null;
  return (
    <Box mt={1} mb={1}>
      <Text fontSize="xs" color="gray.500">{attachmentLabel(attachment, lang)}</Text>
      {failed && (
        <Text fontSize="xs" color="red.500">
          {zh ? '文件无法加载（语音和图片保存 90 天）。' : 'The file could not be loaded (voice messages and images are kept for 90 days).'}
        </Text>
      )}
      {url && attachment.kind === 'voice' && (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript is shown as the message text
        <audio controls preload="metadata" src={url} style={{ width: '100%', maxWidth: 320, marginTop: 4 }}
          aria-label={zh ? '访客语音消息' : 'Visitor voice message'} />
      )}
      {url && attachment.kind === 'image' && (
        <a href={url} target="_blank" rel="noopener noreferrer" title={zh ? '在新标签页打开原图' : 'Open full size in a new tab'}>
          <img src={url} alt={zh ? '访客发送的图片' : 'Image from the visitor'}
            style={{ display: 'block', maxWidth: 240, maxHeight: 240, marginTop: 4, borderRadius: 8, border: '1px solid #e2e8f0' }} />
        </a>
      )}
    </Box>
  );
}
